import { IconName } from '@shared/components/core/images/IconMap';
import type { TFunction } from 'i18next';
import { AgentSkillModel } from '@shared/models/agentExtensions/AgentSkillModel';
import { McpServerRegistrationModel } from '@shared/models/agentExtensions/McpServerRegistrationModel';
import { McpServerModel } from '@shared/models/mcp/McpServerModel';
import { SkillModel } from '@shared/models/skills/SkillModel';

/**
 * Where an extension (skill or MCP server) comes from, used to badge it so users can spot first-party
 * (Cephable), second-party (their organization), third-party (public), and their own local content at a
 * glance — each with a distinct icon + color (mirrors how Control / Create / Automate are colored).
 */
export type ExtensionProvenance = 'mine' | 'cephable' | 'organization' | 'thirdParty';

/**
 * The bare provenance wording — "Cephable" / "Your Organization" / "Third Party" / "Mine". Lives here
 * rather than in the badge so cards can render it as a `descriptionPrefix` pill, where badge chrome and
 * an icon would compete with the card's own type tag.
 */
export function provenanceLabel(provenance: ExtensionProvenance, t: TFunction<'common'>): string {
    switch (provenance) {
        case 'cephable':
            return t('extensions.provenance.cephable');
        case 'organization':
            return t('extensions.provenance.organization');
        case 'thirdParty':
            return t('extensions.provenance.thirdParty');
        default:
            return t('extensions.provenance.mine');
    }
}

/** Brand/valid icon per provenance (Cephable logo, company, globe, user). */
export const PROVENANCE_ICONS: Record<ExtensionProvenance, IconName> = {
    cephable: 'logo',
    organization: 'orgCompany',
    thirdParty: 'globe',
    mine: 'user',
};

/**
 * The provenance fields every installed extension carries. Structural so one classifier serves skills and
 * connectors alike — `SkillSource` and `McpSource` are the same four values.
 */
export type InstalledExtension = Pick<SkillModel | McpServerModel, 'isOfficial' | 'source' | 'organizationId'>;

/** Classify a locally-installed skill or connector. One naming no source can only have been made here. */
export function classifyExtensionProvenance(item: InstalledExtension): ExtensionProvenance {
    if (item.isOfficial) return 'cephable';
    if (item.source === 'user') return 'mine';
    if (item.source === 'organization' || item.organizationId) return 'organization';
    if (item.source === 'marketplace' || item.source === 'imported') return 'thirdParty';
    return 'mine';
}

/**
 * Classify a server-side registration shown in a browse/install list (official / org / public). These
 * are never "mine" — the user's own published items live in their local library instead.
 */
export function classifyRegistrationProvenance(
    server: Pick<AgentSkillModel | McpServerRegistrationModel, 'isOfficial' | 'organizationId'>
): ExtensionProvenance {
    if (server.isOfficial) return 'cephable';
    if (server.organizationId) return 'organization';
    return 'thirdParty';
}
